import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import Modal from "../common/Modal/Modal";
import Papa from "papaparse";
import "./requestrepxbutton.css";
import { formatCSVData, retrieveData } from "../csv/JSONtoCSV";
import { SaveRepXRequestCSV } from "./SaveRepXRequestCSV";
import { CreateRepXExecuteJSON } from "./CreateRepXExecuteJSON";
import { getUnfilledCompulsoryFields } from "../Modals/SubmitCompletedModal/CheckCompletedForm";

/**
 * Handles saving the CSV for the RepX in Snapshots and
 * the creation of a JSON file to be executed by RepX externally.
 */

function RequestRepXButton({setUnfilledFields}) {
  // Initialise use States
  const [openModal, setOpenModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [borderError, setBorderError] = useState(false);
  const [borderErrorMessage, setBorderErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [codeSnippet, setCodeSnippet] = useState("");
  const [taskOptions, setTaskOptions] = useState([]);
  const [parameters, setParameters] = useState("");
  const navigate = useNavigate();
  let selectedSnapshot = localStorage.getItem("selectedSnapshot");
  const getS3 = useOutletContext().getS3;
  const [S3, setS3] = useState(null);

  useEffect(() => {
    getS3().then(setS3);
  }, [])

  //Set codeSnippets to be the task property values
  const fetchUpdatedData = () => {
    const data = JSON.parse(localStorage.getItem("UpdatedDataArray")) || [];
    const taskProperty = data.find((item) => item.Property === "task");
    if (taskProperty) {
      const options = taskProperty["ValueOptions (per property)"].split(",");
      setTaskOptions(options);
      const parametersList = [];
      data.forEach((item) => {
        if (
          item["Category (per property)"] === "TOPICS" &&
          item.Property.includes("topic_")
        ) {
          parametersList.push(
            `${item.Property}=${item["Value (per property)"]}`
          );
        } else if (
          item["Category (per property)"] === "LLM" &&
          item.Property.includes("prompt_")
        ) {
          parametersList.push(
            `${item.Property}=${item["Value (per property)"]}`
          );
        } else if (
          item["Category (per property)"] === "DECISION_TREE" &&
          item.Property.includes("dt_")
        ) {
          parametersList.push(
            `${item.Property}=${item["Value (per property)"]}`
          );
        }
      });
      setParameters(parametersList.join(", "));
    }
  };

  useEffect(() => {
    fetchUpdatedData();
  }, []);

  // Modal Handling Functions
  const handleOpenModal = () => {
    fetchUpdatedData();
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsError(false);
    setIsSuccess(false);
  };

  const selectCodeSnippet = (e) => {
    const selectedValue = e.target.value;
    setCodeSnippet(selectedValue);
    setBorderError(false);
    setBorderErrorMessage("");
  };

   // redirect user to snapshots page after requesting REPX file
   const goBacktoSnapshots = () => {
    localStorage.removeItem("companyNameEditable");
    localStorage.removeItem("formattedDataArray");
    localStorage.removeItem("UpdatedDataArray");
    localStorage.removeItem("companyName");
    localStorage.removeItem("timestamp");
    navigate("/snapshots");
  };

  const handleRequest = async () => {
    if(codeSnippet === "" || codeSnippet === "Select a code snippet..."){
      setBorderError(true);
      setBorderErrorMessage("Please select a code snippet.")
      return;
    }

    const unfilledFields = getUnfilledCompulsoryFields();
    if (unfilledFields.length > 0) {
      setUnfilledFields(unfilledFields);
      setIsError(true);
      setErrorMessage(
        <>
          Your report could not be submitted to HecoAnalytics as you have not
          provided some compulsory (<span style={{ color: "red" }}>*</span>)
          data. Please check your form and ensure all data has been provided.
        </>
      );
      return;
    } 

    try {
      const csvData = await retrieveData();
      const formattedCSVData = formatCSVData(csvData);
      const csvString = Papa.unparse(formattedCSVData);
      console.log(selectedSnapshot)
      await SaveRepXRequestCSV(S3, selectedSnapshot, csvString);
      console.log("codeSnippet1: ", codeSnippet);
      console.log("params1", parameters);
      await CreateRepXExecuteJSON(S3, codeSnippet, parameters);
      setIsSuccess(true);
      goBacktoSnapshots();
    } catch (error) {
      setIsError(true);
      setErrorMessage(
        "There was a problem requesting your RepX. Please try again or email contact@hecoanalytics.com if the issue persists."
      );
      console.log("error in repx request", error);
    }
  
  };

  return (
    <>
      {/* RequestRepX button on Input Form Page */}
      <button className="green-button-repx-request" onClick={handleOpenModal}>
        Request RepX
      </button>

      <Modal
        isOpen={openModal}
        onClose={handleCloseModal}
        title={isError ? "Error Submitting RepX" : "Request a RepX Report"}
        showCustomContent={true}
        customContent={
          isSuccess ? (
            // If submits successfully to S3
            <>
              <div className="modal-success-container">
                <div className="icon-container">
                  <i className="fas fa-check" />
                </div>
                <p className="success-message">
                  You have successfully generated a RepX Request.
                </p>
              </div>
              <div className="button-close-container">
                <button className="button-close" onClick={handleCloseModal}>
                  Okay
                </button>
              </div>
            </>
          ) : isError ? (
            //   It submits unsuccessfully
            <>
              <div className="error-message-repx">
                {errorMessage}</div>
              <div className="button-close-container">
                <button className="button-close" onClick={handleCloseModal}>
                  Close
                </button>
              </div>
            </>
          ) : (
            // Default state -> No success & no error
            <>
              <p className="repx-description">
                Select a code snippet and enter parameters to request report.
              </p>
              <div className="repx-request-body">
                <div className="repx-request-input-container" style={{ borderColor: borderError ? "red" : "" }}>
                  <select onChange={selectCodeSnippet} value={codeSnippet}>
                    <option defaultValue="">Select a code snippet...</option>
                    {taskOptions.map((option, index) => (
                      <option key={index} value={option.trim()}>
                        {option.trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  className="repx-input-params"
                  type="text"
                  value={parameters}
                  readOnly
                />
              </div>
              <div
                className="button-close-container"
                style={{ marginRight: "5px" }}
              >
                {borderError && (
                  <div className="error-message-repx-borderred">{borderErrorMessage}</div>
                )}
                <button className="button-close" onClick={handleRequest}>
                  Request
                </button>
              </div>
            </>
          )
        }
      />
    </>
  );
}

export default RequestRepXButton;
